import { hasCSSSelectorSupport } from "@otto-ec/otto-components-utils/utils/canIUse";
import { mount, unmount } from "svelte";
import { extendElementWithActionAttributes } from "../customElement/extendElementWithActionAttributes.ts";
import type { CustomElement } from "../customElement/utils.ts";
import { useShadowStylesInjector } from "./utils/useShadowStylesInjector.ts";
import { setHost } from "./utils/useHost.ts";
import { createObservedAttributes } from "./utils/createObservedAttributes.ts";
import type { DefineComponentOptions } from "./types.ts";
import { setMounted } from "./utils/setMounted.ts";

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export function defineComponent({
  tag,
  styles,
  props,
  html,
  extendOptions = {},
  component,
}: DefineComponentOptions): void {
  const injectShadowStyles = useShadowStylesInjector(styles);

  const attributeMetaMap: Record<string, { propName: string; type: "String" | "Boolean" }> =
    Object.fromEntries(
      Object.entries(props).map(
        ([propName, { type, attribute = propName }]) => [attribute, { propName, type }] as const,
      ),
    );

  const initialState: Record<string, unknown> = Object.fromEntries(
    Object.entries(props).map(
      ([propName, { type }]) => [propName, type === "Boolean" ? false : undefined] as const,
    ),
  );

  /**
 *
 *
 */
  const reflecting = new WeakSet<HTMLElement>();

  let ElementClass = class extends HTMLElement {
    static observedAttributes = createObservedAttributes(props);

    mounted: boolean = false;
    #ocEffectRootUnset?: () => void;
    $ocProps: Record<string, unknown>;
    $ocSvelteComponent?: Record<string, unknown>;

    constructor() {
      super();
      this.$ocProps = { ...initialState };
    }

    attributeChangedCallback(name: string, _: string | null, newValue: string | null): void {
      if (reflecting.has(this)) return;
      const { propName, type } = attributeMetaMap[name];
      const value = type === "Boolean" ? newValue !== null : (newValue ?? undefined);
      if (this.$ocProps[propName] === value) return;
      this.$ocProps[propName] = value;
      if (this.$ocSvelteComponent) this.$ocSvelteComponent[propName] = value;
    }

    async connectedCallback(): Promise<void> {
      if (html) (this.shadowRoot ? this.shadowRoot : this).innerHTML = html;

      if (component) {
        this.#ocEffectRootUnset = $effect.root(() => {
          const svelteState = $state(this.$ocProps);

          setHost(this);
          this.$ocSvelteComponent = mount(component, {
            target: this.shadowRoot ? this.shadowRoot : this,
            props: svelteState,
          });

          /*                                          */
          Object.assign(this, this.$ocSvelteComponent);
          setHost(undefined);

          /*                                                              */
          /*                                                              */
          Object.keys(props).forEach((propName) => {
            $effect(() => {
              (this as Record<string, unknown>)[propName] = svelteState[propName];
            });
          });
        });
      }

      await injectShadowStyles(this);

      setMounted(tag, this, true);
    }

    async disconnectedCallback(): Promise<void> {
      this.#ocEffectRootUnset?.();
      if (this.$ocSvelteComponent) {
        await unmount(this.$ocSvelteComponent);
        this.$ocSvelteComponent = undefined;
      }

      setMounted(tag, this, false);
    }
  } as unknown as CustomElement;

  Object.entries(props).forEach(([propName, { type, attribute = propName, reflect }]) => {
    Object.defineProperty(ElementClass.prototype, propName, {
      get(this): unknown {
        return this.$ocProps[propName];
      },
      set(this, value: unknown) {
        if (this.$ocProps[propName] === value) return;
        this.$ocProps[propName] = value;
        if (this.$ocSvelteComponent) this.$ocSvelteComponent[propName] = value;

        /*                                                                   */
        /*                                                                   */
        /*                                                                  */
        /*                                                                   */
        /*                                                                    */
        if (reflect) {
          reflecting.add(this);
          if (type === "Boolean") {
            this.toggleAttribute(attribute, Boolean(value));
          } else if (value == null) {
            this.removeAttribute(attribute);
          } else {
            this.setAttribute(attribute, String(value));
          }
          reflecting.delete(this);
        }
      },
    });
  });

  ElementClass = window.__components.extend(extendOptions)(ElementClass);

  /*                                             */
  /*                                                    */
  if (!hasCSSSelectorSupport) ElementClass = extendElementWithActionAttributes(ElementClass);

  customElements.define(tag, ElementClass);
}

window.__components ??= {} as never;
window.__components.defineComponent = defineComponent;
